import React, { useMemo, useState } from 'react';
import { ArrowRight, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { formatDateTime, formatDuration, formatNumber, formatPercent } from './dataDictionaryUtils';

function Kpi({ label, value, sub, tone = 'default', action, onAction }) {
  const color = tone === 'warn' ? 'text-amber-700' : tone === 'info' ? 'text-primary-700' : 'text-[#18181B]';
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-5 py-5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#71717A]">{label}</span>
      <span className={`text-[30px] font-bold leading-[34px] tracking-[-0.02em] tabular-nums ${color}`}>{value}</span>
      <span className="flex items-center justify-between gap-2 text-xs text-[#71717A]">
        <span className="truncate">{sub}</span>
        {action ? (
          <button type="button" onClick={onAction} className="shrink-0 whitespace-nowrap text-[#0062FB] hover:underline">
            {action} →
          </button>
        ) : null}
      </span>
    </div>
  );
}

function Bar({ value, total, color }) {
  const pct = total ? Math.max(0, Math.min(100, (Number(value) / Number(total)) * 100)) : 0;
  return (
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E8EEF5]">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function completenessColor(pct) {
  if (pct >= 95) return '#22c55e';
  if (pct >= 60) return '#f59e0b';
  return '#ef4444';
}

const SORTS = {
  least: { label: 'Least complete', fn: (a, b) => a.pct - b.pct || b.total - a.total },
  most: { label: 'Most complete', fn: (a, b) => b.pct - a.pct || b.total - a.total },
  largest: { label: 'Most columns', fn: (a, b) => b.total - a.total },
  name: { label: 'Name', fn: (a, b) => `${a.schema}.${a.table_name}`.localeCompare(`${b.schema}.${b.table_name}`) },
};

function findDuplicateFolders(folders) {
  // Sibling folder names that only differ by a common suffix/prefix (Geo vs Geographic) or case.
  const dupes = [];
  const walk = (nodes, parent) => {
    const names = nodes.map((n) => n.name);
    for (let i = 0; i < names.length; i += 1) {
      for (let j = i + 1; j < names.length; j += 1) {
        const a = names[i].toLowerCase();
        const b = names[j].toLowerCase();
        if (a === b || (a.length >= 3 && b.length >= 3 && (a.startsWith(b) || b.startsWith(a)))) {
          dupes.push({ parent, a: nodes[i], b: nodes[j] });
        }
      }
    }
    nodes.forEach((n) => walk(n.children || [], [...parent, n.name]));
  };
  walk(folders || [], []);
  return dupes.slice(0, 5);
}

/**
 * Overview tab: KPI tiles, completeness by table, publish history and folder summary.
 * `onBrowse(patch)` deep-links into the Browse tab with a scope/filter.
 */
export default function OverviewView({ meta, publish, onBrowse }) {
  const [sortKey, setSortKey] = useState('least');
  const [showAll, setShowAll] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const overall = meta.stats?.overall;
  const tables = useMemo(() => {
    const list = (meta.stats?.tables || []).map((t) => ({ ...t, pct: t.total ? Math.round((t.complete / t.total) * 100) : 0 }));
    list.sort(SORTS[sortKey].fn);
    return list;
  }, [meta.stats, sortKey]);
  const shown = showAll ? tables : tables.slice(0, 10);
  const dupes = useMemo(() => findDuplicateFolders(meta.folders), [meta.folders]);
  const runs = publish?.status?.runs || [];

  if (!overall) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-card border border-hairline bg-surface p-10 text-sm text-[#71717A] shadow-card">
        {meta.error ? <span className="text-red-600">{meta.error}</span> : <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" aria-hidden /> Loading overview…</span>}
      </div>
    );
  }

  const browse = (scope, filters) => onBrowse({ scope: scope || { type: 'all' }, filters: { q: '', visible: null, complete: null, tag: '', missing: '', ...(filters || {}) }, offset: 0 });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-2">
      <div className="flex divide-x divide-hairline rounded-card border border-hairline bg-surface shadow-card">
        <Kpi label="Columns" value={formatNumber(overall.total)} sub={`across ${formatNumber(new Set((meta.stats?.tables || []).map((t) => t.schema)).size)} schemas · ${formatNumber((meta.stats?.tables || []).length)} tables`} />
        <Kpi label="Visible" value={formatNumber(overall.visible)} sub={`${formatPercent(overall.visible, overall.total)} of columns`} tone="info" action="Browse" onAction={() => browse(null, { visible: true })} />
        <Kpi label="No description" value={formatNumber(overall.missing_description)} sub="shown as help text" tone={overall.missing_description ? 'warn' : 'default'} action={overall.missing_description ? 'Fix' : null} onAction={() => browse(null, { missing: 'description' })} />
        <Kpi label="No folder" value={formatNumber(overall.missing_folder)} sub="hidden from the folder tree" tone={overall.missing_folder ? 'warn' : 'default'} action={overall.missing_folder ? 'Assign' : null} onAction={() => browse({ type: 'nofolder' })} />
        <Kpi label="Untagged" value={formatNumber(overall.missing_tags)} sub="weaker search hits" tone={overall.missing_tags ? 'warn' : 'default'} action={overall.missing_tags ? 'Tag' : null} onAction={() => browse(null, { missing: 'tags' })} />
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-card">
          <div className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-4">
            <div>
              <div className="text-sm font-semibold text-[#18181B]">Completeness by table</div>
              <div className="text-xs text-[#71717A]">Complete = display name, description, folder and at least one tag are set</div>
            </div>
            <div className="relative">
              <button type="button" onClick={() => setSortOpen((o) => !o)} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-surface px-2.5 text-[13px] hover:border-zinc-300" aria-haspopup="listbox" aria-expanded={sortOpen}>
                <span className="text-[#71717A]">Sort</span> <b className="font-semibold">{SORTS[sortKey].label}</b> <ChevronDown size={13} className="text-zinc-500" aria-hidden />
              </button>
              {sortOpen ? (
                <div role="listbox" className="absolute right-0 z-20 mt-1 min-w-[11rem] rounded-xl border border-hairline bg-white p-1 shadow-[0_12px_32px_rgba(24,24,27,0.12)]">
                  {Object.entries(SORTS).map(([k, s]) => (
                    <button key={k} type="button" role="option" aria-selected={k === sortKey} onClick={() => { setSortKey(k); setSortOpen(false); }} className={`flex h-8 w-full items-center rounded-md px-2.5 text-left text-[13px] hover:bg-[#E8EEF5] ${k === sortKey ? 'font-semibold' : 'text-[#3F3F46]'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full table-fixed text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-hairline text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#71717A]">
                  <th className="h-9 w-[180px] px-5">Table</th>
                  <th className="h-9 w-[60px] px-3 text-right">Cols</th>
                  <th className="h-9 w-[150px] px-3">Visible / hidden</th>
                  <th className="h-9 w-[136px] px-3">Complete</th>
                  <th className="h-9 px-3">Gaps</th>
                  <th className="h-9 w-[84px] px-3" />
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => {
                  const scope = { type: 'table', schema: t.schema, table: t.table_name };
                  const gaps = [
                    t.missing_description ? ['description', `${formatNumber(t.missing_description)} desc`] : null,
                    t.missing_folder ? ['folder', `${formatNumber(t.missing_folder)} folder`] : null,
                    t.missing_tags ? ['tags', `${formatNumber(t.missing_tags)} tags`] : null,
                    t.missing_display_name ? ['display_name', `${formatNumber(t.missing_display_name)} name`] : null,
                  ].filter(Boolean);
                  return (
                    <tr key={`${t.schema}.${t.table_name}`} className="border-b border-[#EEF2F7]">
                      <td className="h-11 px-5">
                        <div className="flex flex-col leading-4">
                          <span className="truncate font-semibold">{t.table_name}</span>
                          <span className="truncate font-mono text-[11px] text-[#71717A]">{t.schema}</span>
                        </div>
                      </td>
                      <td className="px-3 text-right tabular-nums">{formatNumber(t.total)}</td>
                      <td className="px-3">
                        <div className="flex items-center gap-2">
                          <Bar value={t.visible} total={t.total} color="#3b82f6" />
                          <span className="w-[58px] text-right text-xs tabular-nums text-[#71717A]">{formatNumber(t.visible)} / {formatNumber(t.hidden)}</span>
                        </div>
                      </td>
                      <td className="px-3">
                        <div className="flex items-center gap-2">
                          <Bar value={t.pct} total={100} color={completenessColor(t.pct)} />
                          <span className="w-[34px] text-right text-xs font-semibold tabular-nums">{t.pct}%</span>
                        </div>
                      </td>
                      <td className="px-3 text-xs text-[#71717A]">
                        {gaps.length ? (
                          <span className="flex flex-wrap gap-x-2">
                            {gaps.map(([missing, label]) => (
                              <button key={missing} type="button" onClick={() => browse(scope, { missing })} className="whitespace-nowrap text-[#0062FB] hover:underline">
                                {label}
                              </button>
                            ))}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 text-right">
                        <button type="button" onClick={() => browse(scope)} className="inline-flex items-center gap-1 text-[13px] text-[#0062FB] hover:underline">
                          Review <ArrowRight size={13} aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tables.length > 10 ? (
            <div className="border-t border-hairline px-5 py-2.5 text-xs text-[#71717A]">
              {showAll ? `All ${tables.length} tables` : `${shown.length} of ${tables.length} tables`} ·{' '}
              <button type="button" onClick={() => setShowAll((s) => !s)} className="text-[#0062FB] hover:underline">
                {showAll ? 'Show fewer' : 'Show all'}
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex w-[360px] shrink-0 flex-col gap-4">
          <div className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-5 shadow-card">
            <div className="text-sm font-semibold text-[#18181B]">Publish history</div>
            {publish?.error && runs.length === 0 ? <p className="text-xs text-red-600">{publish.error}</p> : null}
            {runs.length === 0 && !publish?.error ? <p className="text-xs text-[#71717A]">No publish runs yet.</p> : null}
            <ul className="flex flex-col gap-3 text-[13px]">
              {runs.slice(0, 5).map((r) => (
                <li key={r.run_id} className="flex gap-2.5">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${r.outcome === 'success' ? 'bg-emerald-500' : r.outcome === 'running' ? 'bg-primary-500' : r.outcome === 'failed' ? 'bg-red-500' : 'bg-zinc-300'}`}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <div>
                      {r.outcome === 'success' ? 'Published' : r.outcome === 'running' ? 'Publishing…' : r.outcome === 'failed' ? 'Publish failed' : 'Run'}
                      {r.run_page_url ? (
                        <>
                          {' · '}
                          <a href={r.run_page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[#0062FB] hover:underline">
                            run {r.run_id} <ExternalLink size={10} aria-hidden />
                          </a>
                        </>
                      ) : null}
                    </div>
                    <div className="text-xs text-[#71717A]">
                      {formatDateTime(r.start_time)}
                      {r.duration_ms ? ` · ${formatDuration(r.duration_ms)}` : ''}
                      {r.state_message && r.outcome === 'failed' ? ` · ${r.state_message}` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-5 shadow-card">
            <div className="text-sm font-semibold text-[#18181B]">Folders</div>
            <ul className="flex flex-col gap-2 text-[13px]">
              {(meta.folders || []).map((f) => (
                <li key={f.name} className="flex items-center justify-between gap-2">
                  <button type="button" onClick={() => browse({ type: 'folder', path: f.path })} className="truncate text-left hover:underline">
                    {f.name}
                  </button>
                  <span className="shrink-0 text-xs tabular-nums text-[#71717A]">
                    {formatNumber(f.total)} · {formatNumber((f.children || []).length)} subfolder{(f.children || []).length === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
              {meta.noFolder?.total ? (
                <li className="flex items-center justify-between gap-2 text-amber-700">
                  <button type="button" onClick={() => browse({ type: 'nofolder' })} className="hover:underline">
                    No folder
                  </button>
                  <span className="text-xs tabular-nums">{formatNumber(meta.noFolder.total)}</span>
                </li>
              ) : null}
            </ul>
            {dupes.length ? (
              <div className="border-t border-hairline pt-3 text-xs text-[#71717A]">
                Possible duplicates:{' '}
                {dupes.map((d, i) => (
                  <React.Fragment key={`${d.a.path.join('/')}|${d.b.path.join('/')}`}>
                    {i > 0 ? '; ' : ''}
                    <button type="button" onClick={() => browse({ type: 'folder', path: d.a.path })} className="text-[#0062FB] hover:underline">{d.a.name}</button>
                    {' and '}
                    <button type="button" onClick={() => browse({ type: 'folder', path: d.b.path })} className="text-[#0062FB] hover:underline">{d.b.name}</button>
                    {d.parent.length ? ` under ${d.parent.join(' / ')}` : ''}
                  </React.Fragment>
                ))}
                .
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
